import { LoginState } from "global/LoginState";
import { useFormik } from "formik";
import { AlertState } from "global/AlertState";
import { loadingState } from "global/LoaderState";
import { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import callApi from "services/api";
import { generateSHA512HashAndEncryptInES6 } from "services/encryption";
import * as Yup from "yup";

const ResetPassword = ({ setLoginActiveView }) => {
  const location = useLocation();
  const { username, authenticatedAnswers } = location.state || {};
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useRecoilState(loadingState);
  const setAlertMessage = useSetRecoilState(AlertState);
  const setLoginState = useSetRecoilState(LoginState);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Please provide your password to proceed.")
        .min(8, "Password must be at least 8 characters")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          "Password must contain at least one uppercase letter, one lowercase letter, and one number",
        ),
      confirmPassword: Yup.string()
        .required("Please confirm your password")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      const obj = {
        newPassword: generateSHA512HashAndEncryptInES6(values.password),
        confirmPassword: generateSHA512HashAndEncryptInES6(
          values.confirmPassword,
        ),
        questions: authenticatedAnswers,
        username: username,
      };
      await resetPassword(obj);
    },
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      formik.handleSubmit();
    }
  };

  const resetPassword = async (obj) => {
    setLoading(true);
    var response = await callApi(
      `/v1/api/reset-password`,
      "POST",
      obj,
      true,
      false,
      setAlertMessage,
      navigate,
      setLoginState,
    );
    if (response?.success) {
      setAlertMessage("Password reset successfully. Please login.|true");
      setLoginActiveView("login");
    }
    setLoading(false);
  };

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card mt-3">
            <div className="card-face card-front">
              <div className="card-body">
                <div className="text-center mb-2">
                  <b className="card-title">Reset Password</b>
                </div>

                <form onSubmit={formik.handleSubmit} onKeyDown={handleKeyDown}>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      New Password:<span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        className={`form-control ${
                          formik.touched.password && formik.errors.password
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("password")}
                        placeholder="Enter Password"
                        maxLength={50}
                      />
                      <span
                        className="position-absolute top-50 end-0 translate-middle-y"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          marginRight: formik.errors.password
                            ? "1.99rem"
                            : "1rem",
                        }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <div
                        style={{
                          color: "#f03d3d",
                          fontSize: "0.75rem",
                          marginTop: "0.375rem",
                        }}
                      >
                        {formik.errors.password}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password:<span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-control ${
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("confirmPassword")}
                        placeholder="Confirm Password"
                        maxLength={50}
                      />
                      <span
                        className="position-absolute top-50 end-0 translate-middle-y"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          marginRight: formik.errors.confirmPassword
                            ? "1.99rem"
                            : "1rem",
                        }}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    {formik.touched.confirmPassword &&
                      formik.errors.confirmPassword && (
                        <div
                          style={{
                            color: "#f03d3d",
                            fontSize: "0.75rem",
                            marginTop: "0.375rem",
                          }}
                        >
                          {formik.errors.confirmPassword}
                        </div>
                      )}
                  </div>

                  <div className="d-flex justify-content-center">
                    <button className="btn btn-info w-100" disabled={loading}>
                      Reset
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
